import {
  interpolateGreens,
  interpolateGreys,
  interpolateInferno,
  interpolateOranges,
  interpolatePurples,
  interpolateReds,
  stackOrderAppearance,
  stackOrderAscending,
} from "d3";
import { scaleSequential } from "d3-scale";
import { ClimbType } from "griptonite-api-client";
import sortBy from "lodash/sortBy";

import { Route } from "../../core/types";
import { convertBetweenSystems, convertToInternal } from "../../utils/grades";
import { TraitName, TraitValue, getTraitLabel } from "../../utils/traits";
import { Dimension, GraphDataMap, VenueDataBag } from "./types";

const getGrade = (route: Route, gradeSystem: string) =>
  convertBetweenSystems(route.gradeValue, route.gradeSystem, gradeSystem);

const getCircuit = (route: Route) => {
  // Add a fallback for competition routes, which don't have a circuit but are mutually exclusive with circuits, so can be treated as a "circuit" for the purposes of analysis
  if (route.circuit) {
    return route.circuit.uuid;
  }
  if (route.competitionRouteGroups.length) {
    return "competition:" + route.competitionRouteGroups[0].competition;
  }
  return null;
};

export const getCount = () => 1;
const getStarRating = (route: Route, round?: boolean) => {
  const rating = route.feedback?.averageStarRating;
  if (rating && round) {
    return Math.round(rating * 2) / 2;
  }
  return rating;
};
const getZone = (route: Route) => route.zone?.uuid || null;

const starRatingScale = scaleSequential(interpolateInferno).domain([0, 5.5]);

const boulderColorScales = [
  [14.75, scaleSequential(interpolateGreens).domain([10, 15])],
  [17.5, scaleSequential(interpolateOranges).domain([12, 21])],
  [21, scaleSequential(interpolateReds).domain([14, 22])],
  [25, scaleSequential(interpolatePurples).domain([20, 25])],
  [100, scaleSequential(interpolateGreys).domain([-50, 30])],
] as const;

const sportColorScales = [
  [11, scaleSequential(interpolateGreens).domain([-10, 12])],
  [14, scaleSequential(interpolateOranges).domain([-10, 15])],
  [17, scaleSequential(interpolateReds).domain([0, 15])],
  [23, scaleSequential(interpolatePurples).domain([0, 20])],
  [100, scaleSequential(interpolateGreys).domain([-50, 30])],
] as const;

const determineColorScale = (gradeInternal: number, climbType: "BOULDER" | "SPORT") => {
  const colorScale = climbType === "BOULDER" ? boulderColorScales : sportColorScales;
  for (const [maxGrade, scale] of colorScale) {
    if (gradeInternal <= maxGrade) {
      return scale;
    }
  }
  return boulderColorScales[4][1];
};

// Turns a dimension value, which for traits is one or more ":"-joined values, into a display name.
const getTraitsName = (trait: TraitName, value: string | null, noneLabel: string) =>
  value
    ? value
        .split(":")
        .map((v) => getTraitLabel(trait, v as TraitValue))
        .join(", ")
    : noneLabel;

// Probably the wrong name. What's it called when there are mulitple instances per key?
const postprocessMultiCardinality = (
  dimensionId: string,
  axisData: GraphDataMap,
  breakdownKeys: Set<string | null>,
  bag: VenueDataBag,
) => {
  const values = new Set<string | null>(
    Array.from(breakdownKeys)
      .filter((x) => !!x)
      .join(":")
      .split(":"),
  );
  if (breakdownKeys.has(null)) {
    values.add(null);
  }
  const updatedAxisData: GraphDataMap = new Map();

  const splitCompoundValue = ({
    id,
    name,
    value,
  }: {
    id: string | null;
    name: string;
    value: number;
  }) => {
    if (id === null) {
      return [{ id: null, name, value }];
    }
    const traits = id?.split(":") || [];
    return traits.map((trait) => ({
      id: trait,
      name: trait,
      value: value / traits.length,
    }));
  };

  const splitValuesMap = (
    valuesMap: Map<string | null, { id: string | null; name: string; value: number }>,
  ) => {
    const updatedValuesMap = new Map();
    for (const value of valuesMap.values()) {
      for (const splitValue of splitCompoundValue(value)) {
        updatedValuesMap.set(splitValue.id, {
          id: splitValue.id,
          name: dimensions[dimensionId as DimensionId].getName(splitValue.id, bag),
          value: updatedValuesMap.get(splitValue.id)?.value + splitValue.value || splitValue.value,
        });
      }
    }
    return updatedValuesMap;
  };

  for (const [key, axisDatum] of axisData.entries()) {
    updatedAxisData.set(key, {
      ...axisDatum,
      values: splitValuesMap(axisDatum.values),
    });
  }

  return {
    axisData: updatedAxisData,
    breakdownKeys: values,
  };
};

export const dimensions = {
  circuit: {
    getter: getCircuit,
    label: "Circuit",
    getName: (circuit: string | null, { circuits, competitions }: VenueDataBag) => {
      if (circuit?.startsWith("competition:")) {
        const competitionUuid = circuit.split(":")[1];
        const competition = competitions?.[competitionUuid];
        return competition ? competition.name : "Unknown Event";
      }
      return circuits?.[circuit || ""]?.name ?? "No Circuit";
    },
    getColor: (circuit: string | null, { circuits }: VenueDataBag) => {
      if (circuit?.startsWith("competition:")) {
        return "#0075FF";
      }
      return circuits?.[circuit || ""]?.colors.join(":") ?? "black";
    },
    breakdownStackOrder: stackOrderAppearance,
  },
  zone: {
    getter: getZone,
    label: "Zone",
    getName: (zone: string | null, { zones }: VenueDataBag) =>
      zones?.[zone || ""]?.name ?? "No Zone",
    breakdownStackOrder: stackOrderAppearance,
  },
  line: {
    getter: (route: Route) => route.startLine?.uuid || null,
    label: "Line",
    getName: (line: string | null, { lines }: VenueDataBag) =>
      lines?.[line || ""]?.name ?? "No Line",
    breakdownStackOrder: stackOrderAppearance,
    sortKey: (route) => route.startLine?.name || "",
    sortComparator: (a, b) => {
      const collator = new Intl.Collator("en", {
        numeric: true,
        sensitivity: "base",
      });
      return collator.compare((a || "").toString(), (b || "").toString());
    },
  },
  route: {
    getter: (route: Route) => route.uuid,
    label: "Route",
    getName: (uuid: string | null) => uuid || "?",
    breakdownStackOrder: stackOrderAppearance,
  },
  grade: {
    getter: (route: Route, { venue }) => {
      const gradeSystem =
        route.climbType === "BOULDER"
          ? venue?.boulderGradeSystem || "FONT"
          : venue?.sportGradeSystem || "FRENCH";
      const gradeValue = getGrade(route, gradeSystem);
      if (!gradeValue) {
        return null;
      }
      return `${route.climbType}:${gradeSystem}:${gradeValue}`;
    },
    label: "Grade",
    getName: (grade: string | null) => grade?.split(":")[2] || "No Grade",
    sortKey: (route: Route) => route.gradeInternal || 0,
    getColor: (grade: string | null) => {
      const [climbType, gradeSystem, gradeValue] = grade?.split(":") || [];
      const internal = grade ? convertToInternal(gradeValue, gradeSystem) || 0 : 0;

      return determineColorScale(internal, climbType as ClimbType)(internal);
    },
    breakdownStackOrder: stackOrderAscending,
  },
  starRating: {
    getter: (route: Route) => getStarRating(route, true)?.toFixed(1) || null,
    label: "Star rating",
    getName: (starRating: string | null) => (starRating ? `${starRating}⭐️` : "No Rating"),
    breakdownStackOrder: stackOrderAscending,
    getColor: (starRating: string | null) =>
      starRating !== null ? starRatingScale(Number(starRating)) : "#F1F1F4",
  },
  styles: {
    getter: (route: Route) => (route.styles.length ? sortBy(route.styles).join(":") : null),
    label: "Style",
    getName: (styles: string | null) => getTraitsName("styles", styles, "[No style]"),
    breakdownStackOrder: stackOrderAppearance,
    postprocess: postprocessMultiCardinality,
    // getColor: (style: string | null) => (style ? styleScale(style) : "#F1F1F4"),
    displayAsPercentage: true,
  },
  skills: {
    getter: (route: Route) => (route.skills.length ? sortBy(route.skills).join(":") : null),
    label: "Skill",
    getName: (skills: string | null) => getTraitsName("skills", skills, "[No skill]"),
    breakdownStackOrder: stackOrderAppearance,
    postprocess: postprocessMultiCardinality,
    // getColor: (skill: string | null) => (skill ? skillScale(skill) : "#F1F1F4"),
    displayAsPercentage: true,
  },
  holds: {
    getter: (route: Route) => (route.holds.length ? sortBy(route.holds).join(":") : null),
    label: "Hold",
    getName: (holds: string | null) => getTraitsName("holds", holds, "[No hold]"),
    breakdownStackOrder: stackOrderAppearance,
    postprocess: postprocessMultiCardinality,
    // getColor: (hold: string | null) => (hold ? holdScale(hold) : "#F1F1F4"),
    displayAsPercentage: true,
  },
  setters: {
    getter: (route: Route) =>
      route.setters.length ? sortBy(route.setters.map((x) => x.uuid)).join(":") : null,
    label: "Setter",
    getName: (setter: string | null, { setters }: VenueDataBag) => {
      if (!setter) {
        return "No setter";
      }
      const uuids = setter?.split(":") || [];
      return uuids.map((uuid) => setters?.[uuid]?.name || "Unknown").join(", ");
    },
    postprocess: postprocessMultiCardinality,
    breakdownStackOrder: stackOrderAppearance,
    displayAsPercentage: true,
  },
  ascentStyle: {
    getter: (route: Route) => {
      if (route.autoBelay) {
        return "Auto Belay";
      } else if (route.startLine?.canLead) {
        if (route.startLine?.canToprope) {
          return "Lead + Toprope";
        } else {
          return "Lead only";
        }
      } else {
        return "Toprope only";
      }
    },
    label: "Ascent style",
    getName: (ascentStyle: string | null) => ascentStyle || "No Ascent Style",
    breakdownStackOrder: stackOrderAppearance,
  },
  sportZone: {
    getter: (route: Route) => route.zone?.uuid || null,
    label: "Sector",
    getName: (zone: string | null, { zones }: VenueDataBag) =>
      zones?.[zone || ""]?.name || "No Sector",
    breakdownStackOrder: stackOrderAppearance,
  },
} as const satisfies Record<string, Dimension>;

export type DimensionId = keyof typeof dimensions;

/**
 * What to break each axis value down by. "attemptsCounts" is the attempts
 * metric's own breakdown: it splits each route's value across attempt keys.
 */
export type BreakdownId = DimensionId | "attemptsCounts";

/** The dimension a breakdown groups by, if it's a dimension breakdown. */
export const getBreakdownDimensionId = (breakdown: BreakdownId | null): DimensionId | null =>
  breakdown === "attemptsCounts" ? null : breakdown;

export const getBreakdownDimension = (breakdown: BreakdownId | null) => {
  const dimensionId = getBreakdownDimensionId(breakdown);
  return dimensionId ? dimensions[dimensionId] : null;
};
